/**
 * Web shortcut — builds a Persister backed by `window.localStorage`.
 * Returns `null` outside the browser (SSR consumers don't persist).
 *
 * Consumers who want full control over the persister (custom key,
 * throttle, dehydrate filter, IDB instead of localStorage) build their
 * own via `createSdkPersister` or by constructing the
 * `PlatformAdapter` directly.
 *
 * Most apps don't need this — `createWebDefaultAdapter()` returns an
 * adapter with the localStorage persister already wired in via
 * `adapter.queryPersister`.
 */

import type { Persister } from '../../provider/SdkRuntimeContext.js';

/** Throttle used by `createWebLocalStoragePersister`. */
const WEB_PERSISTER_THROTTLE_MS = 100;

/** Default retention for cached state — TanStack's 24h default. */
const WEB_PERSISTER_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export async function createWebLocalStoragePersister(): Promise<Persister | null> {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  const mod = (await import('@tanstack/query-async-storage-persister')) as unknown as {
    createAsyncStoragePersister: (opts: {
      storage: Storage;
      key?: string;
      throttleTime?: number;
      maxAge?: number;
    }) => Persister;
  };
  return mod.createAsyncStoragePersister({
    storage: window.localStorage,
    key: 'morfi-sdk-cache',
    throttleTime: WEB_PERSISTER_THROTTLE_MS,
    maxAge: WEB_PERSISTER_MAX_AGE_MS,
  });
}
